<template>
  <div class="repru-toolbar-filter d-flex align-items-center">
    <!-- Kreditor -->
    <AppSelect
      v-model="localFilterValues.kreditor"
      :options="filteredKreditoren"
      label="Kreditor"
      placeholder="Kreditor"
      clearable
      class="mr-4"
      style="min-width: 110px"
      @change="onFilterChange"
    />

    <!-- Leistungskürzel -->
    <AppSelect
      v-model="localFilterValues.leistungskuerzel"
      :options="filteredLeistungskuerzel"
      label="Leistungskürzel"
      placeholder="Leistungskürzel"
      clearable
      class="mr-4"
      style="min-width: 160px"
      @change="onFilterChange"
    />

    <!-- Teilleistungskürzel -->
    <AppSelect
      v-model="localFilterValues.teilleistungskuerzel"
      :options="filteredTeilleistungskuerzel"
      label="Teilleistungskürzel"
      placeholder="Teilleistungskürzel"
      clearable
      class="mr-4"
      style="min-width: 180px"
      @change="onFilterChange"
    />

    <!-- BNR -->
    <AppSelect
      v-model="localFilterValues.bnr"
      :options="filteredBnr"
      label="BNR"
      placeholder="BNR"
      clearable
      class="mr-4"
      style="min-width: 80px"
      @change="onFilterChange"
    />

    <!-- Rechnungsnummer -->
    <AppSelect
      v-model="localFilterValues.rechnungsnummer"
      :options="filteredRechnungsnummer"
      label="Rechnungsnummer"
      placeholder="Rechnungsnummer"
      clearable
      class="mr-4"
      style="min-width: 190px"
      @change="onFilterChange"
    />

    <!-- T-VZ Buchen  -->
    <AppSelect
      v-if="page === 'katalog'"
      v-model="localFilterValues.tvzBuchen"
      :options="filteredTvzBuchen"
      label="T-VZ buchen"
      placeholder="T-VZ buchen"
      clearable
      hide-search
      class="mr-4"
      style="min-width: 140px"
      @change="onFilterChange"
    />

    <!-- Filter Reset Button -->
    <div
      v-if="isFilterActive"
      class="icon-button d-flex justify-content-center text-danger mr-4"
      @click="onReset"
      v-b-tooltip.hover.bottom
      title="Filter zurücksetzen"
    >
      <span class="d-flex align-items-center"><FunnelX :size="20" /></span>
    </div>

    <div
      v-if="page === 'katalog' && comments && comments.length > 0"
      class="icon-button d-flex justify-content-center text-danger"
      @click="openKommentareModal"
      v-b-tooltip.hover.bottom
      title="Kommentare"
    >
      <span class="d-flex align-items-center"><MessagesSquare :size="20" /></span>
    </div>

    <!-- Kommentare Modal -->
    <b-modal v-model="showKommentareModal" size="md" title="Kommentare" hide-footer centered>
      <div v-for="(comment, index) in comments" :key="comment.id">
        <div class="comment-item my-2">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <span class="font-weight-bold">
              {{ $store.getters.getUsernameById(comment.creator) }}
            </span>
            <span class="font-weight-bold" style="color: #b5b5c3">
              {{ comment.lastChangeDatetime | dateTime }}
            </span>
          </div>
          <div v-html="comment.text" style="font-size: 12px"></div>
        </div>
        <hr v-if="index < comments.length - 1" class="my-0" />
      </div>
    </b-modal>
  </div>
</template>

<script>
import { FunnelX, MessagesSquare } from 'lucide-vue';
import AppSelect from '@/components/common/app-select/app-select.vue';

export default {
  name: 'RepruToolbarFilter',
  components: {
    AppSelect,
    FunnelX,
    MessagesSquare,
  },
  props: {
    allRows: {
      type: Array,
      default: () => [],
    },
    filterValues: {
      type: Object,
      default: () => ({
        kreditor: null,
        leistungskuerzel: null,
        teilleistungskuerzel: null,
        bnr: null,
        rechnungsnummer: null,
        tvzBuchen: null,
      }),
    },
    page: {
      type: String,
      default: 'katalog',
      validator: value => ['katalog', 'fit'].includes(value),
    },
    comments: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localFilterValues: { ...this.filterValues },
      showKommentareModal: false,
    };
  },
  computed: {
    isFilterActive() {
      return (
        this.localFilterValues.kreditor !== null ||
        this.localFilterValues.leistungskuerzel !== null ||
        this.localFilterValues.teilleistungskuerzel !== null ||
        this.localFilterValues.bnr !== null ||
        this.localFilterValues.rechnungsnummer !== null ||
        this.localFilterValues.tvzBuchen !== null
      );
    },

    filteredKreditoren() {
      return this.getFilterOptions('kreditor');
    },

    filteredLeistungskuerzel() {
      return this.getFilterOptions('leistungskuerzel');
    },

    filteredTeilleistungskuerzel() {
      return this.getFilterOptions('teilleistungskuerzel');
    },

    filteredBnr() {
      return this.getFilterOptions('bnr');
    },

    filteredRechnungsnummer() {
      return this.getFilterOptions('rechnungsnummer');
    },

    filteredTvzBuchen() {
      return (
        this.getFilterOptions('tvzBuchen')
          // sort damit Ja als erste Option
          .sort((a, b) => b - a)
          .map(val => ({ value: val, text: val ? 'Ja' : 'Nein' }))
      );
    },
  },
  watch: {
    filterValues: {
      handler(newVal) {
        this.localFilterValues = { ...newVal };
      },
      deep: true,
    },
  },
  methods: {
    getFilterOptions(field) {
      const { kreditor, leistungskuerzel, teilleistungskuerzel, bnr, rechnungsnummer, tvzBuchen } =
        this.localFilterValues;

      const rows = this.allRows.filter(row => {
        if (field !== 'kreditor' && kreditor && row.kreditor !== kreditor) return false;
        if (field !== 'leistungskuerzel' && leistungskuerzel && row.leistungskuerzel !== leistungskuerzel)
          return false;
        if (
          field !== 'teilleistungskuerzel' &&
          teilleistungskuerzel &&
          row.teilleistungskuerzel !== teilleistungskuerzel
        )
          return false;
        if (field !== 'bnr' && bnr && row.bnr !== bnr) return false;
        // Rechnungsnummer ist ein Array von Nummern -> Zeile matcht, wenn sie die gewaehlte Nummer enthaelt
        if (
          field !== 'rechnungsnummer' &&
          rechnungsnummer &&
          !(Array.isArray(row.rechnungsnummer) && row.rechnungsnummer.includes(rechnungsnummer))
        )
          return false;
        if (field !== 'tvzBuchen' && tvzBuchen != null && !!row.tvzBuchen !== tvzBuchen) return false;
        return true;
      });

      const uniqueValues = [
        ...new Set(
          rows
            .flatMap(row => (Array.isArray(row[field]) ? row[field] : [row[field]]))
            .filter(v => v != null && String(v).trim() !== '')
        ),
      ];
      return uniqueValues.sort();
    },

    onFilterChange() {
      this.$emit('change', { ...this.localFilterValues });
    },

    onReset() {
      this.localFilterValues = {
        kreditor: null,
        leistungskuerzel: null,
        teilleistungskuerzel: null,
        bnr: null,
        rechnungsnummer: null,
        tvzBuchen: null,
      };
      this.$emit('reset');
    },
    openKommentareModal() {
      this.showKommentareModal = true;
    },
  },
};
</script>

<style scoped>
.repru-toolbar-filter {
  --app-select-placeholder-color: var(--text-body);
}
</style>
